import { normalize, schema } from "normalizr";

import PostsService from "../services/PostsService";
import PaymentsService from "../services/PaymentsService";
import StorageService from "../services/StorageService";
import SubscriptionsService from "../services/SubscriptionsService";
import ToastService from "../services/ToastService";
import { localstorageKeys } from "@const/store";
import { noop, removeEmptyKeys } from "./common";
import {
  CCBILL_IFRAME_ID,
  paymentTypes,
  THREE_DS_CANCELED_STATUS,
  THREE_DS_PASSED_STATUS,
} from "@const/payments";
import { decryptCardNumber } from "./encrypt";
import routes from "../const/routes";
import axios from "axios";
import { setDefaultCard } from "@redux/payments/actions";

export const normalizePaymentsArray = (originalSubscription) => {
  const authorSchema = new schema.Entity("authors");
  const subscriptionsSchema = new schema.Entity("subscriptions", {
    author: authorSchema,
  });

  const paymentSchema = new schema.Entity("payments", {
    subscription: subscriptionsSchema,
    user: authorSchema,
  });
  const payments = new schema.Array(paymentSchema);

  return normalize(originalSubscription, payments);
};

export const normalizePromotionPayments = (originalPromotionPayments) => {
  const authorSchema = new schema.Entity("authors");
  const transactionSchema = new schema.Entity("paymentTransaction");
  const paymentSchema = new schema.Entity(
    "payments",
    {
      user: authorSchema,
      paymentTransaction: transactionSchema,
    },
    {
      processStrategy: ({ referral, ...value }) => ({
        ...value,
        user: referral,
      }),
    },
  );

  const payments = new schema.Array(paymentSchema);
  return normalize(originalPromotionPayments, payments);
};

export const handleFiatPostPayment = async ({
  postId,
  cardId,
  navigate = noop,
  from = routes.FEED(),
  defaultCard,
  creatorUniqueKey,
  paymentTokenId,
  threeDSecure,
  setCardData,
  dispatch,
}) => {
  const referral = StorageService.get(
    localstorageKeys.referral,
    {},
  ).referralCode;

  const trackingCode = StorageService.get(localstorageKeys.trackingCode, "");

  const contentOwnerKey = StorageService.get(
    localstorageKeys.referral,
    {},
  ).contentOwnerKey;

  try {
    const { data } = await PostsService.purchasePost(
      postId,
      removeEmptyKeys({
        cardId,
        referral: contentOwnerKey === creatorUniqueKey ? referral : "",
        threeDSecure,
        trackingCode,
      }),
    );

    if (cardId !== defaultCard) dispatch(setDefaultCard({ id: cardId }));

    if (data?.is3DSRequired) {
      await handleThreeDC({
        setCardData,
        paymentTokenId,
        purchasePayment: handleFiatPostPayment,
        cardNumber: data?.card?.cardNumber,
        expMonth: data.card?.expMonth,
        expYear: data.card?.expYear,
        email: data?.card?.email,
        fullName: data?.card?.fullName,
        purchasePaymentParams: {
          postId,
          cardId,
          navigate,
          from,
          defaultCard,
          creatorUniqueKey,
          paymentTokenId,
          threeDSecure,
        },
      });
    } else {
      navigate(from, { replace: true });
    }
  } catch (error) {
    ToastService.error(error.message);
  }
};

export const formatThreeDSResponse = (
  response,
  cardToken = "1234567890123456",
) => {
  console.log("formatThreeDSResponse response --->", response);
  return {
    version: response.protocolVersion,
    acsTransId: response.acsTransId,
    authenticationType: response.authenticationType,
    authenticationValue: response.authenticationValue,
    cardToken: response?.cardToken?.slice(0, 16)
      ? response?.cardToken?.slice(0, 16)
      : cardToken,
    clientTransactionId: response.clientTransactionId,
    dsTransId: response.dsTransId,
    eci: response.eci,
    sdkTransId: response.sdkTransId,
  };
};
export const handleThreeDC = async ({
  setCardData,
  cardNumber,
  expYear,
  expMonth,
  email,
  fullName,
  paymentTokenId,
  purchasePayment,
  purchasePaymentParams,
  formRef,
  handleCancelSecure,
}) => {
  const decryptedCardNumber = await decryptCardNumber(cardNumber);

  setCardData({
    cardNumber: decryptedCardNumber,
    expYear,
    expMonth,
    email,
    fullName,
  });

  const { data: createPaymentTokenData } =
    await PaymentsService.createPaymentToken();

  const customIds = {
    email,
    nameOnCard: fullName,
  };

  const widget = new window.ccbill.CCBillAdvancedWidget(
    import.meta.env.VITE_APP_CCBILL_KEY,
    customIds,
  );
  const resultAuth = await widget.authenticateCustomer(
    createPaymentTokenData.token,
    import.meta.env.VITE_APP_CCBILL_ACCOUNT_NUM,
    import.meta.env.VITE_APP_CCBILL_SUB_ACCOUNT_NUM,
    formRef?.current,
    CCBILL_IFRAME_ID,
    paymentTokenId,
  );

  if (resultAuth.status === THREE_DS_PASSED_STATUS) {
    const threeDSecure = formatThreeDSResponse(resultAuth, paymentTokenId);
    await purchasePayment({
      ...purchasePaymentParams,
      threeDSecure,
    });
  } else if (resultAuth.status === THREE_DS_CANCELED_STATUS) {
    if (resultAuth?.transStatusReasonDetail) {
      ToastService.error(resultAuth.transStatusReasonDetail);
    }
    handleCancelSecure ? handleCancelSecure(resultAuth) : null;
  }
  appendCCBillIframe();
};

export const appendCCBillIframe = () => {
  const iframe = document.createElement("iframe");
  iframe.id = CCBILL_IFRAME_ID;

  const iframeWrapper = document.getElementById("iframeClassWrapper");

  iframeWrapper.appendChild(iframe);
};

export const handleFiatSubPayment = async ({
  subId,
  durationType = 1,
  cardId,
  navigate = noop,
  from = routes.FEED(),
  defaultCard,
  creatorUniqueKey,
  paymentTokenId,
  threeDSecure,
  setCardData,
  dispatch,
}) => {
  try {
    const referral = StorageService.get(
      localstorageKeys.referral,
      {},
    ).referralCode;

    const trackingCode = StorageService.get(localstorageKeys.trackingCode, "");

    const contentOwnerKey = StorageService.get(
      localstorageKeys.referral,
      {},
    ).contentOwnerKey;

    const { data } = await SubscriptionsService.purchaseSubscription(
      subId,
      removeEmptyKeys({
        durationType,
        cardId,
        referral: contentOwnerKey === creatorUniqueKey ? referral : "",
        threeDSecure,
        trackingCode,
      }),
    );

    if (cardId !== defaultCard) dispatch(setDefaultCard({ id: cardId }));

    if (data?.is3DSRequired) {
      await handleThreeDC({
        setCardData,
        paymentTokenId,
        purchasePayment: handleFiatSubPayment,
        cardNumber: data?.card?.cardNumber,
        expMonth: data.card.expMonth,
        expYear: data.card.expYear,
        email: data?.card?.email,
        fullName: data?.card?.fullName,
        purchasePaymentParams: {
          subId,
          durationType,
          cardId,
          navigate,
          from,
          defaultCard,
          creatorUniqueKey,
          paymentTokenId,
        },
      });
    } else {
      navigate(from, { replace: true });
    }
  } catch (error) {
    ToastService.error(error?.message);
  }
};

function checkIsUnitedStatesExist(locations) {
  for (const location of locations) {
    if (location.address_components) {
      for (const component of location.address_components) {
        if (
          component.types.includes("country") &&
          component.long_name === "United States"
        ) {
          return true;
        }
      }
    }
  }
  return false;
}

export const checkIsUserFromUnitedStates = (setIsUserFromUnitedStates) => {
  getCurrentPosition({
    handleSuccess: async (crd) => {
      try {
        const { latitude, longitude } = crd;
        if (latitude && longitude) {
          const { data } = await axios.get(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${import.meta.env.VITE_APP_GOOGLE_MAPS_API_KEY}`,
          );

          const isUnitedStatesExist = checkIsUnitedStatesExist(data.results);
          setIsUserFromUnitedStates(isUnitedStatesExist);
        }
      } catch (e) {
        console.error("checkIsUserFromUnitedStates --->", e);
      }
    },
  });
};

export const getCurrentPosition = ({ handleSuccess }) => {
  function success(pos) {
    const crd = pos.coords;
    // //todo remove after testing
    // console.log("Your current position is:");
    // console.log(`Latitude : ${crd.latitude}`);
    // console.log(`Longitude: ${crd.longitude}`);
    // console.log(`More or less ${crd.accuracy} meters.`);
    handleSuccess(crd);
  }

  function error(err) {
    console.warn(`ERROR(${err.code}): ${err.message}`);
  }

  navigator.geolocation.getCurrentPosition(success, error);
};

export const formatCheckoutData = ({
  paymentType,
  messageUuid,
  streamUniqueKey,
  amount,
  postId,
  subscriptionId,
  durationType,
  message,
  userId,
  isOneTimePayment,
  streamerId,
}) => {
  if (paymentType === paymentTypes.streamTip) {
    return {
      streamUniqueKey,
      amount,
      streamerId,
    };
  }
  if (paymentType === paymentTypes.donation) {
    return {
      userId,
      messageUuid,
      message,
      amount,
      postId,
    };
  }
  if (
    paymentType === paymentTypes.post ||
    (paymentType === paymentTypes.post && postId && isOneTimePayment)
  ) {
    return {
      postId,
    };
  }

  if (paymentType === paymentTypes.subscription) {
    if (!subscriptionId) {
      return;
    }
    return {
      subscriptionId,
      durationType,
    };
  }
};
